import type { ReactNode } from "react";
import { cn } from "@/lib/utils";
import type { LucideIcon } from "lucide-react";
import LogoMark from "@/components/virtualspace/logo-mark";

type Variant = "error" | "not-found" | "unauthorized" | "offline";

type Props = {
  /** Small coordinate/system label above the headline. */
  eyebrow?: string;
  /** Big branded headline. Kept to 2-4 words. */
  title: string;
  /** Supporting sentence — what happened and/or what the user
   *  should do next. */
  description?: ReactNode;
  /** Optional tertiary line — diagnostic detail, e.g. the
   *  error digest from Next's error boundary. Mono font. */
  diagnostic?: ReactNode;
  /** Primary action slot — usually a button or Link back to
   *  safety. Keep to ONE primary action; secondary goes below. */
  primaryAction?: ReactNode;
  /** Optional secondary action — usually a "home" link when the
   *  primary is "retry". */
  secondaryAction?: ReactNode;
  /** Visual tone. Changes the icon tint / background treatment
   *  without changing layout. */
  variant?: Variant;
  /** Override the auto-picked icon. */
  icon?: LucideIcon;
  /** Use the full-canvas treatment for route-level boundaries. */
  immersive?: boolean;
  className?: string;
};

const VARIANT_STYLES: Record<
  Variant,
  { badge: string; ring: string; icon: string }
> = {
  error: {
    badge: "bg-destructive/10",
    ring: "ring-destructive/20",
    icon: "text-destructive",
  },
  "not-found": {
    badge: "bg-muted",
    ring: "ring-muted",
    icon: "text-muted-foreground",
  },
  unauthorized: {
    badge: "vs-accent-bg-soft",
    ring: "ring-[var(--vs-surface-accent-border)]",
    icon: "vs-accent-text",
  },
  offline: {
    badge: "bg-muted",
    ring: "ring-muted",
    icon: "text-muted-foreground",
  },
};

/**
 * Shared full-viewport error surface.
 *
 * Three design decisions baked in so every "something went
 * wrong" page across the app reads as the same brand:
 *
 *   1. Title in Nasalization display at a generous size — this
 *      is a brand moment, even in failure.
 *   2. Diagnostic metadata (the Next.js `error.digest`) goes
 *      into a small mono panel below the description so
 *      support can grep for it without the user wrangling it.
 *   3. ONE primary CTA + at most ONE secondary. Dead-end
 *      screens with 4 buttons are the classic "user panics and
 *      quits" pattern — keep the exits simple.
 *
 * Variants tune only the icon badge colour; the typography and
 * layout stay identical so operators develop pattern
 * recognition across error / not-found / unauthorised /
 * offline surfaces.
 */
export function ErrorScreen({
  eyebrow,
  title,
  description,
  diagnostic,
  primaryAction,
  secondaryAction,
  variant = "error",
  icon: Icon,
  immersive = false,
  className,
}: Props) {
  const v = VARIANT_STYLES[variant];
  return (
    <div
      className={cn(
        "relative isolate flex flex-col items-center justify-center overflow-hidden text-center",
        immersive
          ? "vs-public-dark min-h-[100svh] gap-6 px-6 py-16 text-[var(--text-primary)] vs-canvas-bg vs-grid-bg"
          : "min-h-[60vh] gap-5 px-4",
        className,
      )}
      role={variant === "error" ? "alert" : "status"}
    >
      {immersive && (
        <>
          <div
            className="pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_50%_44%,rgb(var(--vs-accent-rgb)/0.16),transparent_28%)]"
            aria-hidden="true"
          />
          <div
            className="pointer-events-none absolute inset-x-0 bottom-0 h-1/3 bg-gradient-to-t from-black/60 to-transparent"
            aria-hidden="true"
          />
        </>
      )}

      <div
        className="relative flex h-40 w-40 items-center justify-center"
        aria-hidden="true"
      >
        <div
          className={cn(
            "absolute inset-1 rounded-full border border-dashed",
            immersive ? "border-white/15" : "border-border/60",
          )}
        />
        <div
          className={cn(
            "absolute inset-6 rounded-full border",
            immersive ? "border-white/10" : "border-border/40",
          )}
        />
        <div className="absolute inset-1 motion-safe:animate-[spin_14s_linear_infinite]">
          <span
            className={cn(
              "absolute left-1/2 top-0 h-2 w-2 -translate-x-1/2 rounded-full shadow-[0_0_14px_currentColor]",
              v.icon,
            )}
          />
        </div>
        <div
          className={cn(
            "relative flex h-20 w-20 items-center justify-center rounded-2xl border shadow-2xl",
            immersive
              ? "border-white/15 bg-black/70 shadow-black/60"
              : cn(v.badge, v.ring, "ring-1"),
          )}
        >
          <LogoMark size={54} alt="" />
          {Icon && (
            <span
              className={cn(
                "absolute -bottom-2 -right-2 flex h-8 w-8 items-center justify-center rounded-full border",
                immersive
                  ? "border-white/15 bg-(--surface-1)"
                  : cn(v.badge, v.ring, "ring-1"),
              )}
            >
              <Icon className={cn("h-4 w-4", v.icon)} />
            </span>
          )}
        </div>
        <span
          className={cn(
            "absolute left-0 top-1/2 -translate-y-1/2 font-mono text-[9px] tracking-[0.2em]",
            immersive ? "text-white/30" : "text-muted-foreground/50",
          )}
        >
          X
        </span>
        <span
          className={cn(
            "absolute right-0 top-1/2 -translate-y-1/2 font-mono text-[9px] tracking-[0.2em]",
            immersive ? "text-white/30" : "text-muted-foreground/50",
          )}
        >
          Y
        </span>
        <span
          className={cn(
            "absolute bottom-0 left-1/2 -translate-x-1/2 font-mono text-[9px] tracking-[0.2em]",
            immersive ? "text-white/30" : "text-muted-foreground/50",
          )}
        >
          Z
        </span>
      </div>

      <div className="relative max-w-lg space-y-3">
        {eyebrow && <p className="vs-eyebrow">{eyebrow}</p>}
        <h1
          className={cn(
            "vs-font-brand-display tracking-tight",
            immersive ? "text-4xl sm:text-5xl" : "text-2xl",
          )}
        >
          {title}
        </h1>
        {description && (
          <p
            className={cn(
              "text-sm leading-relaxed sm:text-base",
              immersive ? "text-[var(--text-secondary)]" : "text-muted-foreground",
            )}
          >
            {description}
          </p>
        )}
      </div>
      {(primaryAction || secondaryAction) && (
        <div className="relative flex flex-wrap items-center justify-center gap-3 pt-1">
          {primaryAction}
          {secondaryAction}
        </div>
      )}
      {diagnostic && (
        <p
          className={cn(
            "relative max-w-md select-all break-all font-mono text-[10px] tracking-wide",
            immersive ? "text-white/35" : "text-muted-foreground/60",
          )}
        >
          {diagnostic}
        </p>
      )}
    </div>
  );
}
